// Categorize a person by their headline keywords.
// Priority: FOUNDER > INVESTOR > SALES > RECRUITER > OPERATOR > OTHER
// FOUNDER is checked first and trumps all other matches.

export const PERSON_CATEGORIES = [
  { key: 'ALL', label: 'All' },
  { key: 'INVESTOR', label: 'Investors' },
  { key: 'FOUNDER', label: 'Founders' },
  { key: 'OPERATOR', label: 'Operators' },
  { key: 'SALES', label: 'Sales' },
  { key: 'RECRUITER', label: 'Recruiters' },
  { key: 'OTHER', label: 'Other' },
] as const

export function categorizeByHeadline(headline: string): string {
  const lower = (headline || '').toLowerCase()

  // 1. FOUNDER — building/leading a company (checked first, trumps everything)
  const isFounder = /\b(founder|co-founder|cofounder)\b/.test(lower)
    || /\bin stealth\b/.test(lower)
    || /^building\s/.test(lower)
    || (/\b(ceo)\b/.test(lower) && !/\bceo.*sales|sales.*ceo\b/.test(lower))
  if (isFounder) return 'FOUNDER'

  // 2. INVESTOR — professional investors only
  const isInvestor = /\b(venture capital|venture partner|general partner|limited partner)\b/.test(lower)
    || /\b(managing partner|managing director).*(capital|ventures|fund|investment)\b/.test(lower)
    || /\b(partner).*(capital|ventures|fund|vc)\b/.test(lower)
    || /\b(principal).*(capital|fund|ventures)\b/.test(lower)
    || /\bvc\b.*\b(firm|fund|partner)\b/.test(lower)
    || /\b(firm|fund|partner).*\bvc\b/.test(lower)
    || /\b(investor|angel investor)\b/.test(lower)
  if (isInvestor) return 'INVESTOR'

  // 3. SALES — revenue-generating roles
  const isSales = /\b(sales|account executive|account manager)\b/.test(lower)
    || /\b(bdr|sdr)\b/.test(lower)
    || /\b(business development|partnerships manager|demand gen)\b/.test(lower)
    || /\b(chief revenue officer|cro)\b/.test(lower)
    || /\b(growth marketing|revenue)\b/.test(lower)
  if (isSales) return 'SALES'

  // 4. RECRUITER — talent roles
  const isRecruiter = /\b(recruiter|recruiting|talent acquisition|headhunter|staffing)\b/.test(lower)
  if (isRecruiter) return 'RECRUITER'

  // 5. OPERATOR — functional roles at companies
  const isOperator = /\b(engineer|engineering|software|developer|architect|devops|sre|data\s+scientist|machine\s+learning|ml|ai)\b/.test(lower)
    || /\b(product manager|product lead|product director|head of product)\b/.test(lower)
    || /\b(designer|ux|ui\b|design lead|creative director)\b/.test(lower)
    || /\b(marketing|brand|content|communications|pr\b|public relations)\b/.test(lower)
    || /\b(finance|financial|controller|accounting|treasury)\b/.test(lower)
    || /\b(operations|logistics|supply chain)\b/.test(lower)
    || /\b(legal|counsel|compliance|regulatory)\b/.test(lower)
    || /\b(human resources|hr\b|people operations|people partner)\b/.test(lower)
    || /\b(cto|cfo|cmo|cpo|coo|cio|ciso)\b/.test(lower)
    || /\b(vp|vice president|director|head of|senior director|svp|evp)\b/.test(lower)
    || /\b(manager|lead|principal|staff|senior)\b/.test(lower)
    || /\b(analyst|consultant|strategist|advisor)\b/.test(lower)
    || /\b(professor|researcher|phd|scientist|academic)\b/.test(lower)
  if (isOperator) return 'OPERATOR'

  return 'OTHER'
}
